import { useTheme } from '@mui/material/styles';
import {
  CardNumberElement,
  Elements as StripeProvider,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { CouponInput } from '@components/CouponInput';
import {
  FormikDropdownField,
  FormikTelField,
  FormikTextField,
} from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { useCountryRegion } from '@hooks/useCountryRegion';

import CheckoutForm from '@views/Onboarding/PaymentInfo/CheckoutForm';

import type { StripeCoupon } from 'generated/types';

import type { SetupPayment } from '.';

const messages = defineMessages({
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  name: {
    id: 'GLOBAL.name',
    defaultMessage: 'Name',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  line1: {
    id: 'paymentInformationForm.line1',
    defaultMessage: 'Address 1',
  },
  line2: {
    id: 'paymentInformationForm.line2',
    defaultMessage: 'Address 2',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  postalCode: {
    id: 'GLOBAL.postalCode',
    defaultMessage: 'Postal/Zip Code',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/Province',
  },
  couponCode: {
    id: 'paymentInformationForm.couponCode',
    defaultMessage: 'Coupon Code',
  },
  submit: {
    id: 'GLOBAL.next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  tokenMissing: {
    id: 'paymentInfo.stripeTokenMissing',
    defaultMessage: 'Unable to facilitate payment',
  },
  invalidCreditCard: {
    id: 'paymentInfo.invalidCreditCard',
    defaultMessage: 'Invalid credit card information',
  },
});

const PaymentInformationForm: React.FC<{
  initialPayment: SetupPayment;
  onBack?: () => void;
  onSubmit?: (values: SetupPayment) => void;
}> = ({ initialPayment, onBack, onSubmit }) => {
  const stripe = useStripe();
  const elements = useElements();

  const {
    palette: { primary },
  } = useTheme();

  const { formatMessage } = useIntl();
  const { countryOptions, getProvinceOptions } = useCountryRegion();

  const [couponCode, setCouponCode] = useState<StripeCoupon | null>(null);

  // Validation schema
  const paymentInformationSchema = Yup.object().shape({
    name: Yup.string().required(formatMessage(messages.requiredText)),
    phone: Yup.string(),
    line1: Yup.string().required(formatMessage(messages.requiredText)),
    line2: Yup.string(),
    city: Yup.string().required(formatMessage(messages.requiredText)),
    province: Yup.string().required(formatMessage(messages.requiredText)),
    postalCode: Yup.string().required(formatMessage(messages.requiredText)),
    country: Yup.string().required(formatMessage(messages.requiredText)),
  });

  // Intentional/out of scope for VEG-655: non-US accounts (including the
  // newly-selectable international countries) currently charge in CAD, confirmed by product.
  function getCurrency(country?: string) {
    return country === 'US' ? 'usd' : 'cad';
  }

  async function handleSubmit(
    values: SetupPayment,
    { setSubmitting, setErrors }: FormikHelpers<SetupPayment>
  ) {
    if (!stripe || !elements) {
      setSubmitting(false);
      setErrors({ token: formatMessage(messages.tokenMissing) });
      return;
    }
    const card = elements.getElement(CardNumberElement);
    if (!card) {
      setSubmitting(false);
      setErrors({ token: formatMessage(messages.invalidCreditCard) });
      return;
    }

    const { token } = await stripe.createToken(card, {
      name: values.name,
      address_line1: values.line1,
      address_line2: values.line2,
      address_city: values.city,
      address_state: values.province,
      address_zip: values.postalCode,
      address_country: values.country,
      currency: getCurrency(values.country),
    });

    if (token && onSubmit) {
      onSubmit({ ...values, token, coupon: couponCode });
    }

    setSubmitting(false);
  }

  // Return form
  return (
    <Formik
      initialValues={initialPayment}
      onSubmit={handleSubmit}
      validationSchema={paymentInformationSchema}
    >
      {({ isSubmitting, setFieldValue, values }) => {
        const provinceOptions = getProvinceOptions(values.country);

        return (
          <Form>
            <FormikTextField
              label={formatMessage(messages.name)}
              name="name"
              required
            />

            <FormikTextField
              label={formatMessage(messages.line1)}
              name="line1"
              required
            />

            <FormikTextField
              label={formatMessage(messages.line2)}
              name="line2"
            />

            <FormikTextField
              label={formatMessage(messages.city)}
              name="city"
              required
            />

            <FormikDropdownField
              label={formatMessage(messages.country)}
              labelColor={primary.main}
              name="country"
              onChange={(value) => {
                setFieldValue('country', value);
                // Reset the province/region, since it's specific to the
                // previously selected country
                setFieldValue('province', '');
                // Clear the phone number so a stale calling code from the
                // previously selected country isn't submitted
                setFieldValue('phone', '');
              }}
              options={countryOptions}
              required
            />

            <FormikDropdownField
              label={formatMessage(messages.region)}
              labelColor={primary.main}
              name="province"
              onChange={(value) => {
                setFieldValue('province', value);
              }}
              options={provinceOptions}
              required
            />

            <FormikTextField
              label={formatMessage(messages.postalCode)}
              name="postalCode"
              required
            />

            <FormikTelField
              country={values.country}
              label={formatMessage(messages.phone)}
              name="phone"
            />

            <CheckoutForm />

            <CouponInput showCheckoutMsg onVerify={setCouponCode} />

            <RoundedButton disabled={isSubmitting} type="submit">
              {formatMessage(messages.submit)}
            </RoundedButton>

            <RoundedButton onClick={onBack} type="button">
              {formatMessage(messages.back)}
            </RoundedButton>
          </Form>
        );
      }}
    </Formik>
  );
};

const PaymentInformation: React.FC<{
  initialPayment: SetupPayment;
  onSubmit?: (values: any) => void;
  onBack?: () => void;
}> = ({ initialPayment, onSubmit, onBack }) => {
  const stripePromise = loadStripe(process.env.GATSBY_STRIPE_PUBLIC_KEY ?? '');

  return (
    <StripeProvider stripe={stripePromise}>
      <PaymentInformationForm
        initialPayment={initialPayment}
        onBack={onBack}
        onSubmit={onSubmit}
      />
    </StripeProvider>
  );
};

export default PaymentInformation;
